/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

/**
 * @module bulb-block-alignment/bulbblockalignmentcommand
 */

import { Command, type Editor } from 'ckeditor5';

/**
 * The block alignment command. It is used to apply different alignments to the content blocks.
 *
 * @extends Command
 */
export default class BulbBlockAlignmentCommand extends Command {

  styles: any;

  defaultStyle: any;

  constructor(editor: Editor, styles) {
    super(editor);

    /**
     * The name of the default style, if it is present. If there is no default style, it defaults to `false`.
     *
     * @readonly
     * @type {Boolean|String}
     */
    this.defaultStyle = false;

    /**
     * A style handled by this command.
     *
     * @readonly
     * @member #styles
     */
    this.styles = styles.reduce((styles, style) => {
      styles[style.name] = style;

      if (style.isDefault) {
        this.defaultStyle = style.name;
      }

      return styles;
    }, {});
  }

  /**
   * @inheritDoc
   */
  public override refresh(): void {
    const element = this.editor.model.document.selection.getSelectedElement();

    this.isEnabled = true;

    if (!element) {
      this.value = false;
    } else if (element.hasAttribute('blockAlignment')) {
      const attributeValue = element.getAttribute('blockAlignment') as string;
      this.value = this.styles[attributeValue] ? attributeValue : false;
    } else {
      this.value = this.defaultStyle;
    }
  }

  /**
   * Executes the command.
   *
   *    editor.execute( 'blockAlignment', { value: 'side' } );
   *
   * @param {Object} options
   */
  public override execute(options: { value: string; }): void {
    const styleName = options.value;

    const { model } = this.editor;
    const blockElement = model.document.selection.getSelectedElement();

    model.change((writer) => {
      // Default style means that there is no `imageStyle` attribute in the model.
      // https://github.com/ckeditor/ckeditor5-image/issues/147
      if (this.styles[styleName].isDefault) {
        writer.removeAttribute('blockAlignment', blockElement);
      } else {
        writer.setAttribute('blockAlignment', styleName, blockElement);
      }
    });
  }
}
